import { UsersApiService } from "../api/UsersService";
import { Constants } from "../constants";
import { LogoutReason } from "../models/authenticated-user.interface";
import {
  ILoginResult,
  LoginFailureReason,
} from "../models/results/login-result.interface";
import { IResult } from "../models/results/result.interface";
import Role from "../models/role";
import {
  getLoginFailureReason,
  getLogoutUrlFromToken,
  getRoleFromServerRoleAndOrg,
} from "../utils/authorizationUtil";
import LocalStorageUtil from "../utils/localStorage";
import logger from "../utils/logger";
import { browserHistory } from "../utils/routes.util";
import authorizationService from "./authorization.service";
import ecgsService from "./ecgs.service";

const login = async (
  email: string,
  password: string,
  ssoToken?: string
): Promise<ILoginResult> => {
  try {
    const data = await UsersApiService.usersLogin({
      credentials: !ssoToken ? { email, password } : { token: ssoToken },
    });

    const { error, response: serverUser } = data;

    if (error || !serverUser) {
      return {
        success: false,
        message: error?.errorMessage,
        failReason: getLoginFailureReason(error?.errorCode?.toString() ?? ""),
      };
    }

    const role = getRoleFromServerRoleAndOrg(
      serverUser.roleId ?? 0,
      serverUser.organization?.organizationType
    );

    if (
      !serverUser.canLogIntoPortal ||
      role === Role.NonProfessional ||
      role === Role.OrgNonProfessional
    ) {
      return {
        success: false,
        message: "User can't login to portal",
        failReason: LoginFailureReason.Blocked,
      };
    }

    const user = authorizationService.createAuthenticatedUser({
      ...serverUser,
      IsSso: !!ssoToken,
    });

    return {
      success: true,
      data: user,
    };
  } catch (error) {
    return {
      success: false,
      message: (error as Error).message,
      failReason: LoginFailureReason.Unknown,
    };
  }
};

const authenticateApiUser = async (
  email: string,
  password: string,
): Promise<IResult<string>> => {
  try {
    const response = await UsersApiService.authenticateApiUser({ credentials: { email, password } });

    if (!response.data) {
      const failureReasonCode = getLoginFailureReason(response.status.toString())
      return { success: false, message: failureReasonCode.toString() };
    }

    return { success: true, data: response.data };
  } catch (error) {
    return { success: false };
  }
};

const logout = (type: LogoutReason) => {
  const lockedEcgId = LocalStorageUtil.get(Constants.REVIEW_LOCKED_ECG_ID_KEY);
  if (lockedEcgId) {
    /**
     * If an ecg was locked by a reviewer,
     * we must release it on logout
     * (adding last used token since token is removed until request is actually executed)
     */
    const lastUsedToken = LocalStorageUtil.get(Constants.JWT_KEY);
    ecgsService.unlockEcg(lockedEcgId, lastUsedToken ?? "");
    LocalStorageUtil.remove([Constants.REVIEW_LOCKED_ECG_ID_KEY]);
  }

  const logoutRedirectUrl = getLogoutUrlFromToken(type);

  LocalStorageUtil.set(Constants.LOGOUT_REASON_KEY, type);

  browserHistory.push(logoutRedirectUrl);
  LocalStorageUtil.remove([Constants.JWT_KEY]);
};

const changePassword = async (
  currentPassword: string,
  newPassword: string
): Promise<boolean> => {
  logger.info(changePassword.name, "Changing password...");

  try {
    const { response: resp, error } = await UsersApiService.usersChangePassword(
      {
        passwordDto: {
          oldPassword: currentPassword,
          newPassword,
        },
      }
    );

    if (error || !resp) {
      logger.error(changePassword.name, "Invalid API response", {
        resp,
        error,
      });
      return false;
    }

    return true;
  } catch (error) {
    logger.error(changePassword.name, "Error updating password.", error);
    return false;
  }
};

const forgotPassword = async (
  email: string,
  mobile?: number
): Promise<IResult<boolean>> => {
  logger.info(forgotPassword.name, `Forgot password request`);
  try {
    const { response: resp, error } = await UsersApiService.usersForgotPassword(
      {
        forgotPasswordDto: { email },
      }
    );

    if (error || !resp) {
      logger.error(forgotPassword.name, "Invalid API response", {
        resp,
        error,
      });
      return { success: false, message: error?.errorMessage };
    }

    return { success: true };
  } catch (error) {
    logger.error(forgotPassword.name, "Error forgot password.", error);
    return { success: false, message: (error as Error).message };
  }
};

const resetPassword = async (
  token: string,
  newPassword: string
): Promise<IResult<boolean>> => {
  logger.info(resetPassword.name, `Reset password request`);

  try {
    const {
      response: resp,
      error,
    } = await UsersApiService.usersResetPasswordFromEmail({
      resetPasswordDto: {
        token,
        newPassword,
      },
    });

    if (error || !resp) {
      logger.error(resetPassword.name, "Invalid API response", {
        resp,
        error,
      });
      return { success: false, message: error?.errorMessage, errorCode: error?.errorCode?.toString() as string | undefined };
    }

    return { success: true };
  } catch (error) {
    logger.error(resetPassword.name, "Error reset password.", error);
    return { success: false, message: (error as Error).message };
  }
};

const authService = {
  login,
  logout,
  authenticateApiUser,
  changePassword,
  forgotPassword,
  resetPassword,
};

export default authService;
